import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search, ClipboardList, Trash2, X, Check, BookOpen } from 'lucide-react';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const FALLBACK_IMG = '/ije-fallback.svg';
const thumbImg = (u) => {
  if (!u) return FALLBACK_IMG;
  if (u.startsWith('/api/images/')) {
    const sep = u.includes('?') ? '&' : '?';
    return `${API_BASE}${u}${sep}thumb=1`;
  }
  if (u.startsWith('/api/')) return `${API_BASE}${u}`;
  return u;
};
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * New Order dialog — admin picks a customer, adds line items (search or bulk
 * paste), fills shipping + notes, then saves as draft or fires off for
 * customer approval in one click.
 */
export default function NewOrderDialog({ open, onOpenChange, onCreated }) {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [items, setItems] = useState([]); // [{sku, name, brand, qty}]
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogLoadingFor, setCatalogLoadingFor] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUserId(''); setItems([]); setBulkText(''); setBulkOpen(false);
    setShippingAddress(''); setNotes(''); setAdminNotes('');
    setUserQuery(''); setProductQuery(''); setProductResults([]);
    api.get('/admin/users', { params: { status: 'active' } }).then((r) => setUsers(r.data || []));
    api.get('/catalogs', { params: { limit: 200 } })
      .then((r) => setCatalogs(r.data?.items || []))
      .catch(() => {});
  }, [open]);

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.business_name || '').toLowerCase().includes(q) ||
      (u.contact_name || '').toLowerCase().includes(q)
    );
  }, [users, userQuery]);

  useEffect(() => {
    if (!open || !productQuery.trim()) { setProductResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/products', { params: { q: productQuery.trim(), limit: 12 } });
        setProductResults(data.items || []);
      } catch { setProductResults([]); } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [productQuery, open]);

  const selectedUser = users.find((u) => u.id === userId);

  const addItem = (p) => {
    setItems((cur) => {
      const existing = cur.find((i) => i.sku === p.sku);
      if (existing) {
        // If the item is already in the cart, tapping Add again bumps qty
        // by one — turns the search list into a rapid-add stepper.
        return cur.map((i) => i.sku === p.sku ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...cur, { sku: p.sku, name: p.name, brand: p.brand, qty: p.min_order_qty || 1 }];
    });
    // Keep the search results open so Priscila can keep adding without
    // retyping — but clear only when the search field is empty.
  };

  const removeItem = (sku) => setItems((cur) => cur.filter((i) => i.sku !== sku));
  const changeQty = (sku, q) => {
    const n = Math.max(1, parseInt(q, 10) || 1);
    setItems((cur) => cur.map((i) => i.sku === sku ? { ...i, qty: n } : i));
  };
  // Compact preview of the running subtotal (using the buyer's discount).
  const subtotal = useMemo(() => {
    if (!items.length || !selectedUser) return 0;
    const disc = Number(selectedUser.discount_percent || 0);
    // We don't have full product data in `items`, so pull it from productResults
    // when the item is visible there. Otherwise the backend recomputes on save.
    return items.reduce((s, i) => {
      const p = productResults.find((r) => r.sku === i.sku);
      if (!p) return s;
      return s + Number(p.wholesale_price || 0) * (1 - disc / 100) * i.qty;
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedUser, productResults]);

  const bulkAdd = async () => {
    const raw = bulkText.split(/[\n,\t;]+/).map((s) => s.trim()).filter(Boolean);
    if (!raw.length) return;
    try {
      const { data } = await api.post('/products/lookup', { skus: raw });
      const existing = new Set(items.map((i) => i.sku));
      const additions = (data.found || [])
        .filter((p) => !existing.has(p.sku))
        .map((p) => ({ sku: p.sku, name: p.name, brand: p.brand, qty: p.min_order_qty || 1 }));
      if (additions.length) setItems((cur) => [...cur, ...additions]);
      const missing = data.missing || [];
      const bits = [];
      if (additions.length) bits.push(`${additions.length} added`);
      if (missing.length) bits.push(`${missing.length} not found`);
      toast[additions.length ? 'success' : 'info'](bits.join(' · ') || 'Nothing to add');
      setBulkText('');
      setBulkOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk add failed');
    }
  };

  /**
   * Load every item from a saved catalog into the cart. Fixes the case
   * where a catalog is named after its contents (e.g. "ID lubricants") but
   * the items inside carry a different `brand` field so typing the catalog
   * name into product search misses them entirely.
   */
  const loadCatalog = async (c) => {
    setCatalogLoadingFor(c.id);
    try {
      const { data } = await api.get(`/catalogs/${c.slug}`);
      const skus = (data.products || []).map((p) => p.sku).filter(Boolean);
      if (!skus.length) {
        toast.info('This catalog has no items');
        return;
      }
      const { data: hydrated } = await api.post('/products/lookup', { skus });
      const existing = new Set(items.map((i) => i.sku));
      const additions = (hydrated.found || [])
        .filter((p) => !existing.has(p.sku))
        .map((p) => ({ sku: p.sku, name: p.name, brand: p.brand, qty: p.min_order_qty || 1 }));
      if (additions.length) setItems((cur) => [...cur, ...additions]);
      const missing = (hydrated.missing || []).length;
      const bits = [];
      if (additions.length) bits.push(`${additions.length} added`);
      const already = skus.length - additions.length - missing;
      if (already > 0) bits.push(`${already} already in cart`);
      if (missing) bits.push(`${missing} unavailable`);
      toast[additions.length ? 'success' : 'info'](bits.join(' · ') || 'Nothing to add');
      setCatalogPickerOpen(false);
      setCatalogQuery('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setCatalogLoadingFor(''); }
  };

  const filteredCatalogs = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    if (!q) return catalogs;
    return catalogs.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.slug || '').toLowerCase().includes(q),
    );
  }, [catalogs, catalogQuery]);

  const save = async (thenSubmit) => {
    if (!userId) return toast.error('Pick a customer first');
    if (!items.length) return toast.error('Add at least one product');
    setBusy(true);
    try {
      const payload = {
        user_id: userId,
        items: items.map((i) => ({ sku: i.sku, qty: i.qty })),
        shipping_address: shippingAddress,
        notes, admin_notes: adminNotes,
      };
      const { data: created } = await api.post('/admin/orders', payload);
      if (thenSubmit) {
        await api.post(`/admin/orders/${created.id}/submit-for-approval`);
        toast.success(`Order ${created.order_number} sent to ${created.user_email}`);
      } else {
        toast.success(`Draft ${created.order_number} saved`);
      }
      onCreated?.(created);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-16px)] max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4 !grid-cols-[minmax(0,1fr)]"
        data-testid="new-order-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif text-xl sm:text-2xl">New order</DialogTitle>
          <p className="sans text-[10px] sm:text-xs tracking-widest uppercase text-neutral-500">Draft on behalf of a customer</p>
        </DialogHeader>

        <div className="space-y-6 py-3">
          {/* Customer */}
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Customer</label>
            {selectedUser ? (
              <div className="flex items-center justify-between border border-neutral-200 px-3 py-3 bg-neutral-50 gap-3">
                <div className="min-w-0 flex-1">
                  <p className="serif text-base leading-tight">{selectedUser.business_name || selectedUser.email}</p>
                  <p className="sans text-sm text-neutral-700 truncate mt-0.5">{selectedUser.email}</p>
                  <p className="sans text-xs text-[color:var(--ije-pink-dark)] mt-1">{Number(selectedUser.discount_percent || 0)}% off wholesale</p>
                </div>
                <button type="button" onClick={() => setUserId('')} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black shrink-0">Change</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  {!userQuery && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />}
                  <input
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder="Search approved customers…"
                    className="ije-input h-10 w-full pl-9"
                    data-testid="new-order-customer-search"
                  />
                </div>
                {filteredUsers.length > 0 && (
                  <ul className="mt-2 border border-neutral-200 max-h-56 overflow-y-auto divide-y divide-neutral-100">
                    {filteredUsers.slice(0, 20).map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => setUserId(u.id)}
                          className="w-full text-left px-3 py-2.5 hover:bg-neutral-50"
                          data-testid={`new-order-pick-user-${u.id}`}
                        >
                          <p className="sans text-sm text-neutral-900">{u.business_name || u.email}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="sans text-xs text-neutral-500 truncate">{u.email}</p>
                            <span className="sans text-[11px] text-[color:var(--ije-pink-dark)] shrink-0">· {Number(u.discount_percent || 0)}% off</span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Products */}
          <div>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500">Products</label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => { setCatalogPickerOpen((v) => !v); setBulkOpen(false); }} className="sans text-[11px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid="new-order-load-catalog-toggle">
                  <BookOpen size={13} /> {catalogPickerOpen ? 'Close catalogs' : 'Load from catalog'}
                </button>
                <button type="button" onClick={() => { setBulkOpen((v) => !v); setCatalogPickerOpen(false); }} className="sans text-[11px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1">
                  <ClipboardList size={13} /> {bulkOpen ? 'Close bulk paste' : 'Bulk paste SKUs'}
                </button>
              </div>
            </div>
            {catalogPickerOpen && (
              <div className="border border-neutral-200 bg-neutral-50 p-3 mb-3" data-testid="new-order-catalog-picker">
                <div className="relative mb-2">
                  {!catalogQuery && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />}
                  <input
                    value={catalogQuery}
                    onChange={(e) => setCatalogQuery(e.target.value)}
                    placeholder="Search your catalogs by name…"
                    className="ije-input h-10 w-full pl-9"
                    data-testid="new-order-catalog-search"
                  />
                </div>
                {filteredCatalogs.length === 0 ? (
                  <p className="sans text-xs text-neutral-500 py-2">No catalogs match.</p>
                ) : (
                  <ul className="max-h-56 overflow-y-auto divide-y divide-neutral-100 bg-white border border-neutral-200">
                    {filteredCatalogs.map((c) => {
                      const count = (c.item_skus || []).length;
                      return (
                        <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                          <div className="flex-1 min-w-0">
                            <p className="serif text-sm truncate">{c.name}</p>
                            <p className="sans text-[11px] text-neutral-500 mt-0.5">
                              {count} item{count === 1 ? '' : 's'}
                              {c.pricing_mode === 'discounted' && c.discount_pct ? ` · ${c.discount_pct}% off wholesale` : ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={catalogLoadingFor === c.id || count === 0}
                            onClick={() => loadCatalog(c)}
                            className="btn-primary h-9 shrink-0 disabled:opacity-40"
                            data-testid={`new-order-load-catalog-${c.id}`}
                          >
                            {catalogLoadingFor === c.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            {catalogLoadingFor === c.id ? 'Loading' : 'Load'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            {bulkOpen && (
              <div className="border border-neutral-200 bg-neutral-50 p-3 space-y-2 mb-3">
                <textarea
                  rows={3}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder={"Paste SKUs — one per line or separated by commas."}
                  className="ije-input w-full p-3 resize-none text-sm font-mono"
                />
                <div className="flex justify-end">
                  <button type="button" onClick={bulkAdd} disabled={!bulkText.trim()} className="btn-primary h-9">Add all</button>
                </div>
              </div>
            )}
            <div className="relative">
              {!productQuery && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />}
              <input
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                placeholder="Search products by name, SKU, or brand…"
                className="ije-input h-10 w-full pl-9"
                data-testid="new-order-product-search"
              />
              {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-neutral-400" />}
            </div>
            {productResults.length > 0 && (
              <ul className="mt-2 border border-neutral-200 max-h-80 overflow-y-auto divide-y divide-neutral-100" data-testid="new-order-product-results">
                {productResults.map((p) => {
                  const inCart = items.find((i) => i.sku === p.sku);
                  const discount = Number(selectedUser?.discount_percent || 0);
                  const buyerPrice = Number(p.wholesale_price || 0) * (1 - discount / 100);
                  return (
                    <li key={p.sku} className="flex items-center gap-3 px-3 py-2.5">
                      <img
                        src={thumbImg((p.images || [])[0])}
                        alt=""
                        className="w-12 h-12 object-cover border border-neutral-200 flex-shrink-0 bg-white"
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.target.src = FALLBACK_IMG; }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="sans text-sm text-neutral-900 truncate">{p.name}</p>
                        <p className="sans text-[11px] text-neutral-500 truncate mt-0.5">
                          <span className="uppercase tracking-widest">{p.brand}</span>
                          <span className="mx-1.5">·</span>
                          <span className="font-mono">{p.sku}</span>
                        </p>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="serif text-sm text-[color:var(--ije-pink-dark)]">{fmtMoney(buyerPrice)}</span>
                          {p.msrp ? <span className="sans text-[11px] text-neutral-400 line-through">{fmtMoney(p.msrp)}</span> : null}
                          {p.min_order_qty > 1 ? <span className="sans text-[10px] text-neutral-500 tracking-widest uppercase ml-1">min {p.min_order_qty}</span> : null}
                        </div>
                      </div>
                      {inCart ? (
                        <span className="inline-flex items-center gap-1 sans text-[10px] tracking-widest uppercase px-2 py-1 border border-neutral-900 bg-neutral-900 text-white shrink-0">
                          <Check size={12} /> {inCart.qty} added
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => addItem(p)}
                          className="btn-primary h-9 shrink-0"
                          data-testid={`new-order-add-product-${p.sku}`}
                        >
                          <Plus size={14} /> Add
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {items.length > 0 && (
              <ul className="mt-3 border border-neutral-200 divide-y divide-neutral-100" data-testid="new-order-items">
                {items.map((i, idx) => (
                  <li key={i.sku} className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2">
                    <span className="sans text-[10px] text-neutral-400 w-5 sm:w-6 text-right shrink-0">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="sans text-sm text-neutral-900 truncate">{i.name}</p>
                      <p className="sans text-[11px] text-neutral-500 truncate mt-0.5">
                        <span className="uppercase tracking-widest">{i.brand}</span>
                        <span className="mx-1.5">·</span>
                        <span className="font-mono">{i.sku}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button type="button" onClick={() => changeQty(i.sku, i.qty - 1)} className="w-8 h-9 border border-neutral-200 sans text-lg active:bg-neutral-100" aria-label="Decrease">−</button>
                      <input
                        type="number" min="1"
                        value={i.qty}
                        onChange={(e) => changeQty(i.sku, e.target.value)}
                        className="ije-input h-9 w-12 text-center"
                        data-testid={`new-order-qty-${i.sku}`}
                      />
                      <button type="button" onClick={() => changeQty(i.sku, i.qty + 1)} className="w-8 h-9 border border-neutral-200 sans text-lg active:bg-neutral-100" aria-label="Increase">+</button>
                    </div>
                    <button type="button" onClick={() => removeItem(i.sku)} className="p-2 -mr-1 text-neutral-400 hover:text-red-600 active:bg-red-50 shrink-0" title="Remove">
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Shipping + notes */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Shipping address</label>
              <textarea
                rows={4}
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder="Street, City, State ZIP"
                className="ije-input w-full p-3 resize-none"
                data-testid="new-order-shipping"
              />
            </div>
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Notes for customer</label>
              <textarea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="ije-input w-full p-3 resize-none"
                data-testid="new-order-notes"
              />
            </div>
          </div>
          <div>
            <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">
              Admin notes <span className="normal-case tracking-normal text-neutral-400 ml-1">(private — for DJ order entry)</span>
            </label>
            <textarea
              rows={2}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="ije-input w-full p-3 resize-none"
              data-testid="new-order-admin-notes"
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
          <button type="button" onClick={() => onOpenChange(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto">
            <X size={14} className="inline mr-1" /> Cancel
          </button>
          <button type="button" onClick={() => save(false)} disabled={busy} className="btn-outline h-11 sm:h-10 w-full sm:w-auto" data-testid="new-order-save-draft">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save draft
          </button>
          <button type="button" onClick={() => save(true)} disabled={busy} className="btn-primary h-11 sm:h-10 w-full sm:w-auto" data-testid="new-order-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save + send for approval
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
